import { useAppDispatch } from '@hooks/reduxHooks'
import SearchIcon from '@mui/icons-material/Search'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
} from '@mui/material'
import {
  DataGridPro,
  GridColDef,
  GridSelectionModel,
} from '@mui/x-data-grid-pro'
import { Actions } from '@state/actions'
import { useSelector } from '@state/store'
import {
  assignExamsToRoom,
  findAll,
  findExamsAssignedToRoom,
} from '@state/thunks/procedureThunk'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'

type ExamAssignmentDialogProps = {
  examRoomId: number
  open: boolean
  onClose: () => void
}

const columns: GridColDef[] = [
  { field: 'code', headerName: 'Code', width: 140 },
  { field: 'label', headerName: 'Libellé', flex: 1 },
  { field: 'modality', headerName: 'Modalité', width: 120 },
]

export function ExamAssignmentDialog({
  examRoomId,
  open,
  onClose,
}: ExamAssignmentDialogProps) {
  const dispatch = useAppDispatch()
  const allExams = useSelector(({ procedure }) => procedure.exams.datas)
  const assignedExamIds = useSelector(
    ({ procedure }) => procedure.assignedExamIds,
  )
  const loading = useSelector(
    ({ loading }) => !!loading[Actions.PROCEDURE_FIND_ALL],
  )

  const [page, setPage] = useState(0)
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([])
  const [search, setSearch] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setPage(0)
    setSearch('')
    dispatch(findAll({}))
    dispatch(findExamsAssignedToRoom(examRoomId))
  }, [open, examRoomId])

  useEffect(() => {
    setSelectionModel(assignedExamIds)
  }, [assignedExamIds])

  const filteredSortedExams = useMemo(() => {
    const query = search.toLowerCase()
    const filtered = query
      ? allExams.filter(
          (e) =>
            e.code?.toLowerCase().includes(query) ||
            e.label?.toLowerCase().includes(query),
        )
      : allExams
    return [
      ...filtered.filter((e) => selectionModel.includes(e.id)),
      ...filtered.filter((e) => !selectionModel.includes(e.id)),
    ]
  }, [allExams, search, selectionModel, setSearch])

  const handleSave = async () => {
    setSaving(true)
    try {
      await dispatch(
        assignExamsToRoom({
          examRoomId,
          examIds: selectionModel as number[],
        }),
      )
      enqueueSnackbar('Examens assignés avec succès', { variant: 'success' })
      onClose()
    } catch {
      enqueueSnackbar('Erreur lors de l\'assignation des examens', {
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle>Assigner des examens à la salle {examRoomId}</DialogTitle>
      <DialogContent
        sx={{ height: 520, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <TextField
          size="small"
          placeholder="Rechercher par code ou libellé…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <DataGridPro
          rows={filteredSortedExams}
          columns={columns}
          checkboxSelection
          selectionModel={selectionModel}
          onSelectionModelChange={setSelectionModel}
          keepNonExistentRowsSelected
          density="compact"
          disableColumnMenu
          loading={loading}
          pagination
          page={page}
          pageSize={25}
          onPageChange={setPage}
          rowsPerPageOptions={[25]}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Annuler
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? <CircularProgress size={20} /> : 'Sauvegarder'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
